/**
 * CHG-053 REQ-V53-019: 필드 타입별 동적 렌더링 컴포넌트
 */
import { ExternalLink } from 'lucide-react';

interface FieldRendererProps {
  fieldKey?: string;
  fieldType: string;
  name: string;
  options?: string[] | null;
  value: unknown;
  onChange?: (value: unknown) => void;
  readOnly?: boolean;
  required?: boolean;
  members?: { id: string; name: string }[];
}

export default function FieldRenderer({
  fieldKey,
  fieldType,
  name,
  options,
  value,
  onChange,
  readOnly = false,
  required = false,
  members,
}: FieldRendererProps) {
  void fieldKey; // used as key by parent
  const label = (
    <label className="block text-sm font-medium text-gray-500 mb-1">
      {name}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );

  if (readOnly) {
    return (
      <div>
        {label}
        <ReadOnlyValue fieldType={fieldType} value={value} options={options} members={members} />
      </div>
    );
  }

  const baseClass = 'w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

  switch (fieldType) {
    case 'TEXT':
      return (
        <div>
          {label}
          <input
            type="text"
            value={(value as string) ?? ''}
            onChange={(e) => onChange?.(e.target.value)}
            className={baseClass}
          />
        </div>
      );

    case 'TEXTAREA':
      return (
        <div className="col-span-2">
          {label}
          <textarea
            value={(value as string) ?? ''}
            onChange={(e) => onChange?.(e.target.value)}
            rows={3}
            className={`${baseClass} resize-none`}
          />
        </div>
      );

    case 'NUMBER':
      return (
        <div>
          {label}
          <input
            type="number"
            value={value !== null && value !== undefined ? String(value) : ''}
            onChange={(e) => onChange?.(e.target.value ? Number(e.target.value) : null)}
            className={baseClass}
          />
        </div>
      );

    case 'SELECT':
      return (
        <div>
          {label}
          <select
            value={(value as string) ?? ''}
            onChange={(e) => onChange?.(e.target.value || null)}
            className={baseClass}
          >
            <option value="">선택하세요</option>
            {(options ?? []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );

    case 'MULTI_SELECT': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div>
          {label}
          <div className="flex flex-wrap gap-1 p-1.5 border border-gray-200 rounded-lg min-h-[34px]">
            {(options ?? []).map((opt) => {
              const isChecked = selected.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    const next = isChecked ? selected.filter((v) => v !== opt) : [...selected, opt];
                    onChange?.(next);
                  }}
                  className={`text-sm px-2 py-0.5 rounded-full border transition-colors ${
                    isChecked
                      ? 'bg-blue-100 border-blue-300 text-blue-700'
                      : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    case 'DATE':
      return (
        <div>
          {label}
          <input
            type="date"
            value={(value as string)?.slice(0, 10) ?? ''}
            onChange={(e) => onChange?.(e.target.value || null)}
            className={baseClass}
          />
        </div>
      );

    case 'URL':
      return (
        <div>
          {label}
          <div className="flex gap-1">
            <input
              type="url"
              value={(value as string) ?? ''}
              onChange={(e) => onChange?.(e.target.value)}
              placeholder="https://"
              className={`${baseClass} flex-1`}
            />
            {typeof value === 'string' && value && (
              <a href={value} target="_blank" rel="noopener noreferrer" className="p-1.5 text-gray-400 hover:text-blue-500">
                <ExternalLink size={14} />
              </a>
            )}
          </div>
        </div>
      );

    case 'CHECKBOX':
      return (
        <div className="flex items-center gap-2 py-1">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange?.(e.target.checked)}
            className="w-4 h-4 accent-blue-600"
          />
          <label className="text-sm text-gray-700">{name}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
        </div>
      );

    case 'USER':
      return (
        <div>
          {label}
          <select
            value={(value as string) ?? ''}
            onChange={(e) => onChange?.(e.target.value || null)}
            className={baseClass}
          >
            <option value="">미배정</option>
            {(members ?? []).map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>
      );

    default:
      return (
        <div>
          {label}
          <input
            type="text"
            value={String(value ?? '')}
            onChange={(e) => onChange?.(e.target.value)}
            className={baseClass}
          />
        </div>
      );
  }
}

function ReadOnlyValue({
  fieldType,
  value,
  options,
  members,
}: {
  fieldType: string;
  value: unknown;
  options?: string[] | null; // eslint-disable-line @typescript-eslint/no-unused-vars
  members?: { id: string; name: string }[];
}) {
  void options; // reserved for future SELECT display
  if (value === null || value === undefined || value === '') {
    return <span className="text-sm text-gray-300">—</span>;
  }

  switch (fieldType) {
    case 'URL':
      return (
        <a href={value as string} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
          {(value as string).replace(/^https?:\/\//, '').slice(0, 40)}
          <ExternalLink size={12} />
        </a>
      );
    case 'CHECKBOX':
      return <span className="text-sm">{value ? '✓' : '✗'}</span>;
    case 'MULTI_SELECT':
      return (
        <div className="flex flex-wrap gap-1">
          {(value as string[]).map((v) => (
            <span key={v} className="text-sm px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">{v}</span>
          ))}
        </div>
      );
    case 'USER': {
      const member = members?.find((m) => m.id === value);
      return <span className="text-sm text-gray-800">{member?.name ?? (value as string)}</span>;
    }
    case 'DATE':
      return <span className="text-sm text-gray-800">{(value as string).slice(0, 10)}</span>;
    case 'TEXTAREA':
      return <p className="text-sm text-gray-800 whitespace-pre-wrap">{value as string}</p>;
    default:
      return <span className="text-sm text-gray-800">{String(value)}</span>;
  }
}
